import { Link, Navigate, createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import {
  AuthDivider,
  AuthErrorAlert,
  AuthInput,
  AuthLayout,
  AuthSubmitButton,
  CaptchaWidget,
  OAuthButton,
  isCaptchaConfigured,
} from '@/features/auth/components'
import { useOAuthSignIn, useSignIn } from '@/features/auth/hooks'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { generateLinks, generateMetaTags, routeSEO } from '@/lib/seo'
import { useSession } from '@/lib/auth-client'

export const Route = createFileRoute('/sign-in')({
  head: () => ({
    meta: generateMetaTags(routeSEO['/sign-in']),
    links: generateLinks(routeSEO['/sign-in']),
  }),
  component: SignInPage,
})

const LINK_CLASS =
  'font-semibold text-primary underline-offset-4 hover:underline'

function SignInPage() {
  const { data: session, isPending: isSessionPending } = useSession()
  const searchParams =
    typeof window !== 'undefined'
      ? new URLSearchParams(window.location.search)
      : null

  const continueParam =
    searchParams?.get('continue') || searchParams?.get('redirect')

  const redirectAfterAuth = continueParam ? continueParam : '/dashboard'

  const { signInWithEmail, isLoading, error } = useSignIn(redirectAfterAuth)
  const { signInWithOAuth, isLoading: isGoogleLoading } = useOAuthSignIn()
  const [captchaToken, setCaptchaToken] = useState<string>()

  const handleSubmit = async (e: React.SubmitEvent<HTMLFormElement>) => {
    e.preventDefault()
    const formData = new FormData(e.currentTarget)

    await signInWithEmail({
      email: formData.get('email') as string,
      password: formData.get('password') as string,
      rememberMe: formData.get('remember') === 'on',
      captchaToken,
    })
  }

  const handleGoogleSignIn = () => {
    signInWithOAuth('google', redirectAfterAuth)
  }

  if (isSessionPending) return null
  if (session?.user) return <Navigate to={redirectAfterAuth} />

  return (
    <AuthLayout
      endpoint="/api/auth/sign-in/email"
      title="Welcome back"
      description="Sign in to access your API dashboard"
      footer={
        <>
          Don't have an account?{' '}
          <Link
            to="/sign-up"
            search={continueParam ? { continue: continueParam } : {}}
            className={LINK_CLASS}
          >
            Create account
          </Link>
        </>
      }
    >
      <AuthErrorAlert error={error} />

      <OAuthButton
        provider="google"
        onClick={handleGoogleSignIn}
        isLoading={isGoogleLoading}
        disabled={isLoading}
      />

      <AuthDivider text="Or sign in with email" />

      <form onSubmit={handleSubmit} className="space-y-5">
        <AuthInput
          type="email"
          id="email"
          name="email"
          label="Email"
          placeholder="you@company.com"
          icon="mail"
          required
          disabled={isLoading}
        />

        <div>
          <div className="mb-2 flex items-center justify-between">
            <Label htmlFor="password">Password</Label>
            <Link
              to="/forgot-password"
              className={`text-body-sm ${LINK_CLASS}`}
            >
              Forgot password?
            </Link>
          </div>
          <AuthInput
            type="password"
            id="password"
            name="password"
            placeholder="Enter your password"
            icon="lock"
            required
            disabled={isLoading}
            showPasswordToggle
          />
        </div>

        <div className="flex items-center gap-2.5">
          <Checkbox id="remember" name="remember" className="accent-primary" />
          <Label
            htmlFor="remember"
            className="font-normal text-[var(--color-text-secondary)]"
          >
            Remember me for 30 days
          </Label>
        </div>

        {/* Bot/spam protection */}
        <CaptchaWidget
          onVerify={setCaptchaToken}
          onExpire={() => setCaptchaToken(undefined)}
        />

        <AuthSubmitButton
          isLoading={isLoading}
          disabled={isGoogleLoading || (isCaptchaConfigured && !captchaToken)}
        >
          Sign In
        </AuthSubmitButton>
      </form>
    </AuthLayout>
  )
}
